import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { useAuth } from "../auth/AuthProvider";
import Goal from "../models/Goal";
import GoalAttempt from "../models/GoalAttempt";

const GoalDetailPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { goalId } = useParams();

  const [goal, setGoal] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && goalId) {
      loadData();
    }
  }, [user, goalId]);

  const loadData = async () => {
    try {
      const [goalData, attemptList] = await Promise.all([
        Goal.getById(user.uid, goalId),
        GoalAttempt.getByGoalId(user.uid, goalId),
      ]);

      setGoal(goalData);
      setAttempts(attemptList);

      if (goalData) {
        const statsData = await GoalAttempt.getStats(user.uid, goalId, goalData);
        setStats(statsData);
      }
    } catch (err) {
      console.error("Error loading goal:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + "T12:00:00");
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const formatFullDate = (dateStr) => {
    const date = new Date(dateStr + "T12:00:00");
    return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  // Build chart data with expected progress line
  const buildChartData = () => {
    if (!goal) return [];

    const startDate = goal.startDate?.toDate ? goal.startDate.toDate() : new Date(goal.startDate);
    const deadline = goal.deadline?.toDate ? goal.deadline.toDate() : new Date(goal.deadline);
    const startValue = goal.startingValue || 0;
    const targetValue = goal.targetValue;

    // Create data points for expected progress line
    const data = [];

    // Add start point
    data.push({
      date: formatDate(startDate.toISOString().split("T")[0]),
      fullDate: startDate.toISOString().split("T")[0],
      expected: startValue,
      actual: null,
      isExpected: true,
    });

    // Add attempt points
    const sortedAttempts = [...attempts].reverse();
    sortedAttempts.forEach((attempt) => {
      const attemptDate = new Date(attempt.date + "T12:00:00");
      const totalDays = (deadline - startDate) / (1000 * 60 * 60 * 24);
      const elapsedDays = (attemptDate - startDate) / (1000 * 60 * 60 * 24);
      const expectedAtDate = goal.direction === "increase"
        ? startValue + (targetValue - startValue) * (elapsedDays / totalDays)
        : startValue - (startValue - targetValue) * (elapsedDays / totalDays);

      data.push({
        date: formatDate(attempt.date),
        fullDate: attempt.date,
        expected: Math.round(expectedAtDate * 10) / 10,
        actual: attempt.value,
        isPB: attempt.isPersonalBest,
      });
    });

    // Add deadline point
    data.push({
      date: formatDate(deadline.toISOString().split("T")[0]),
      fullDate: deadline.toISOString().split("T")[0],
      expected: targetValue,
      actual: null,
      isExpected: true,
    });

    return data;
  };

  const chartData = buildChartData();

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">{formatFullDate(data.fullDate)}</p>
          {data.actual !== null && (
            <p className="text-lg font-bold text-indigo-600">
              Actual: {data.actual} {goal?.targetUnit}
              {data.isPB && <span className="ml-2 text-yellow-500">PR!</span>}
            </p>
          )}
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Expected: {data.expected} {goal?.targetUnit}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          Loading...
        </div>
      </div>
    );
  }

  if (!goal) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          Goal not found
        </div>
      </div>
    );
  }

  const currentValue = stats?.currentValue ?? goal.startingValue ?? 0;
  const progress = Goal.calculateProgress(goal, currentValue);
  const daysRemaining = Goal.getDaysRemaining(goal);
  const paceStatus = Goal.getPaceStatus(goal, currentValue);
  const isCompleted = goal.status === "completed";

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <button
        onClick={() => navigate("/goals")}
        className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6"
      >
        <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden mb-6">
        {goal.img && (
          <div className="h-40 sm:h-48 overflow-hidden">
            <img
              src={goal.img}
              alt={goal.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="p-6">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {goal.title}
            </h1>
            {isCompleted ? (
              <span className="px-3 py-1 text-sm font-medium bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-full">
                Completed
              </span>
            ) : (
              <span className={`px-3 py-1 text-sm font-medium ${paceStatus.bgColor} ${paceStatus.color} rounded-full`}>
                {paceStatus.label}
              </span>
            )}
          </div>

          {goal.description && (
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              {goal.description}
            </p>
          )}
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Deadline: {formatTimestamp(goal.deadline)}
          </p>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {currentValue}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Current ({goal.targetUnit})</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-indigo-600">
                {goal.targetValue}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Target ({goal.targetUnit})</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {isCompleted ? "0" : daysRemaining}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Days Left</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-center">
              <div className={`text-2xl font-bold ${paceStatus.color}`}>
                {paceStatus.label}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Status</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600 dark:text-gray-400">Progress</span>
              <span className="font-medium text-gray-900 dark:text-white">{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
              <div
                className={`h-3 rounded-full ${isCompleted ? "bg-green-500" : "bg-indigo-600"}`}
                style={{ width: `${Math.min(100, progress)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Progress Chart */}
      {attempts.length >= 1 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Progress vs Expected
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#9CA3AF", fontSize: 12 }}
                  axisLine={{ stroke: "#374151" }}
                  tickLine={{ stroke: "#374151" }}
                />
                <YAxis
                  tick={{ fill: "#9CA3AF", fontSize: 12 }}
                  axisLine={{ stroke: "#374151" }}
                  tickLine={{ stroke: "#374151" }}
                  domain={[
                    goal.direction === "increase"
                      ? (goal.startingValue || 0) * 0.9
                      : goal.targetValue * 0.9,
                    goal.direction === "increase"
                      ? goal.targetValue * 1.1
                      : (goal.startingValue || goal.targetValue * 2) * 1.1
                  ]}
                />
                <Tooltip content={<CustomTooltip />} />
                {/* Expected progress line (dashed) */}
                <Line
                  type="monotone"
                  dataKey="expected"
                  stroke="#9CA3AF"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  connectNulls
                />
                {/* Actual progress line */}
                <Line
                  type="monotone"
                  dataKey="actual"
                  stroke="#6366f1"
                  strokeWidth={2}
                  dot={{ fill: "#6366f1", r: 5 }}
                  activeDot={{ r: 7, fill: "#6366f1" }}
                  connectNulls
                />
                {/* Target line */}
                <ReferenceLine
                  y={goal.targetValue}
                  stroke="#22c55e"
                  strokeDasharray="3 3"
                  label={{ value: "Target", fill: "#22c55e", fontSize: 12 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-0.5 bg-gray-400" style={{ borderTop: "2px dashed #9CA3AF" }} />
              <span className="text-gray-500 dark:text-gray-400">Expected</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-0.5 bg-indigo-600" />
              <span className="text-gray-500 dark:text-gray-400">Actual</span>
            </div>
          </div>
        </div>
      )}

      {/* Attempt History */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Attempt History
        </h2>

        {attempts.length === 0 ? (
          <p className="text-center text-gray-500 dark:text-gray-400 py-8">
            No attempts logged yet. Start by logging your first attempt!
          </p>
        ) : (
          <div className="space-y-3">
            {attempts.map((attempt, index) => (
              <div
                key={attempt.id}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {formatFullDate(attempt.date)}
                    {index === attempts.length - 1 && goal.startingValue && (
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(baseline)</span>
                    )}
                  </div>
                  {attempt.notes && (
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {attempt.notes}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-gray-900 dark:text-white">
                    {attempt.value} {goal.targetUnit}
                    {attempt.reps && ` x${attempt.reps}`}
                    {attempt.isPersonalBest && (
                      <span className="ml-2 text-yellow-500 text-sm">PR!</span>
                    )}
                  </div>
                  {attempt.perceivedEffort && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      RPE: {attempt.perceivedEffort}/10
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Log Attempt Button */}
      {!isCompleted && (
        <div className="text-center">
          <button
            onClick={() => navigate(`/log-goal-attempt?goal=${goalId}`)}
            className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
          >
            Log Attempt
          </button>
        </div>
      )}
    </div>
  );
};

export default GoalDetailPage;
